import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import RequiredNoteConfirmationDialog from '../../../components/RequiredNoteConfirmationDialog';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { CrewMemberCertificate } from '../../../types/crew';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const CrewRejectionDialog = ({
  crewMemberId,
  certificateId,
  onClose,
  onConfirm,
}: {
  crewMemberId: string | null;
  certificateId: string | null;
  onClose: () => void;
  onConfirm: (ccertificate: CrewMemberCertificate) => void;
}) => {
  const { rejectCrewMemberCertificate } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  const rejectCrewMutation = useMutation({
    mutationFn: (note: string) =>
      rejectCrewMemberCertificate(
        crewMemberId || '',
        certificateId || '',
        note,
      ),
    onSuccess: certificate => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.certificate-rejection-success'),
      });
      onConfirm(certificate);
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(
          error,
          t('content.certificate-rejection-error'),
        ),
      });
      rejectCrewMutation.reset();
    },
  });

  const handleClose = () => {
    if (rejectCrewMutation.isPending) {
      return;
    }
    onClose();
  };

  return (
    <RequiredNoteConfirmationDialog
      open={Boolean(crewMemberId)}
      title={t('content.reject-crew-certificate')}
      noteLabel={t('content.rejection-note-reason')}
      onConfirmRequest={note => {
        if (crewMemberId) {
          rejectCrewMutation.mutate(note, {});
        }
      }}
      onCloseRequest={handleClose}
      loading={rejectCrewMutation.isPending}
      disabled={rejectCrewMutation.isPending}
    />
  );
};

export default CrewRejectionDialog;
